import React from 'react';
import { motion } from 'framer-motion';

interface Project {
  id: number;
  title: string;
  client: string;
  description: string;
  results: string;
  image: string;
}

const FeaturedProjects: React.FC = () => {
  // Projects from the case study and portfolio
  const projects: Project[] = [
    {
      id: 1,
      title: "Content Creation Automation",
      client: "Blancc Media",
      description: "Custom AI assistant that breaks down 3-4 hour long YouTube podcasts into Twitter posts, long forms, and threads.",
      results: "Saved 17-20 hours/week with standardized content creation process",
      image: "content-automation.jpg" // Placeholder
    },
    {
      id: 2,
      title: "Competitor Analysis Automation",
      client: "Blancc Media",
      description: "Automation that analyzes competitors' Twitter profiles, gathering top performing posts based on engagement metrics.",
      results: "Helped outperform influencers in the same niche with data-driven insights",
      image: "competitor-analysis.jpg" // Placeholder
    },
    {
      id: 3,
      title: "Meeting-to-Content Automation",
      client: "Various Clients",
      description: "System that scrapes meeting transcripts and converts them to content in the client's tone and voice.",
      results: "Eliminated manual content creation from meetings, saving hours of work",
      image: "meeting-content.jpg" // Placeholder
    },
    {
      id: 4,
      title: "Personalized AI Assistant",
      client: "Coaching Businesses",
      description: "AI persona bot that replicates experience & personality, serving as a downsell option for leads who can't afford full coaching.",
      results: "Increased revenue through additional service tier with minimal overhead",
      image: "ai-persona.jpg" // Placeholder
    }
  ];

  return (
    <div className="gradient-bg py-20">
      <div className="container mx-auto px-4">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-bold text-center mb-4"
        >
          Featured Automations
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-xl text-gray-300 text-center max-w-3xl mx-auto mb-16"
        >
          Discover how our AI automations have transformed operations and driven growth for our clients.
        </motion.p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-black/30 backdrop-blur-sm rounded-xl overflow-hidden"
            >
              <div className="h-48 bg-gray-800 flex items-center justify-center">
                <span className="text-2xl font-bold text-gradient">{project.title}</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                <p className="text-sm text-accent mb-4">Client: {project.client}</p>
                <p className="text-gray-300 mb-4">{project.description}</p>
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                  <p className="text-accent font-medium">Results: {project.results}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedProjects;
